import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../../../components/SEO';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * GOOGLE ADS CONVERSION TRACKING — SETUP INSTRUCTIONS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * 1. In your Google Ads account go to:
 *    Goals → Conversions → + New conversion action → Website
 *
 * 2. Set the conversion page URL to:
 *    https://yourdomain.com/quote/thank-you
 *
 * 3. Copy your Conversion ID and Conversion Label from Google Ads and
 *    replace the placeholder values below:
 *
 *    const GOOGLE_ADS_CONVERSION_ID    = 'AW-XXXXXXXXX';   ← your ID
 *    const GOOGLE_ADS_CONVERSION_LABEL = 'XXXXXXXXXXXX';   ← your label
 *
 * 4. (Optional) Set CONVERSION_VALUE to the average lead value in CAD.
 *
 * 5. The gtag() call fires automatically when this page loads.
 *    No extra setup needed — Google Ads will record a conversion every time
 *    a visitor lands on /quote/thank-you.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const GOOGLE_ADS_CONVERSION_ID    = 'AW-XXXXXXXXX';   // ← Replace with your Conversion ID
const GOOGLE_ADS_CONVERSION_LABEL = 'XXXXXXXXXXXX';   // ← Replace with your Conversion Label
const CONVERSION_VALUE            = 150;              // ← Average lead value in CAD (optional)
const CONVERSION_CURRENCY         = 'CAD';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const CITY_NAMES: Record<string, string> = {
  toronto: 'Toronto', mississauga: 'Mississauga', brampton: 'Brampton',
  oakville: 'Oakville', hamilton: 'Hamilton', vaughan: 'Vaughan',
  'richmond-hill': 'Richmond Hill', whitby: 'Whitby', ajax: 'Ajax', oshawa: 'Oshawa',
};

const NEXT_STEPS = [
  { icon: 'ri-user-search-line', title: 'Specialist Assigned', desc: 'A certified local security expert is reviewing your submission right now.' },
  { icon: 'ri-file-list-3-line', title: 'Custom Quote Prepared', desc: 'We build a detailed, itemized quote tailored to your property — no generic packages.' },
  { icon: 'ri-phone-line', title: 'We Contact You Within 2 Hours', desc: 'Expect a call or email from your specialist during business hours.' },
  { icon: 'ri-shield-check-line', title: 'Installation Scheduled', desc: 'Once you approve the quote, we book your installation — often same week.' },
];

const TRUST_ITEMS = [
  { icon: 'ri-lock-2-line', label: 'Your info is 100% private' },
  { icon: 'ri-thumb-up-line', label: 'No obligation, ever' },
  { icon: 'ri-time-line', label: '2-hour response guarantee' },
  { icon: 'ri-award-line', label: 'BBB A+ Rated since 1999' },
];

export default function QuoteThankYouPage() {
  const [searchParams] = useSearchParams();
  const cityParam = (searchParams.get('city') || 'toronto').toLowerCase().replace(/\s+/g, '-');
  const cityName  = CITY_NAMES[cityParam] || 'Your City';
  const firedRef  = useRef(false);

  /* ── Fire Google Ads conversion on mount (once) ── */
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    // Push to dataLayer for GTM compatibility
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'conversion',
        conversionId: GOOGLE_ADS_CONVERSION_ID,
        conversionLabel: GOOGLE_ADS_CONVERSION_LABEL,
        value: CONVERSION_VALUE,
        currency: CONVERSION_CURRENCY,
        city: cityName,
      });
    }

    // Direct gtag call (works if Google tag is loaded via index.html)
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_CONVERSION_LABEL}`,
        value: CONVERSION_VALUE,
        currency: CONVERSION_CURRENCY,
        transaction_id: `quote-${Date.now()}`,
      });
    }

    // Fallback: inject noscript pixel if gtag not yet loaded
    const img = document.createElement('img');
    img.height = 1;
    img.width  = 1;
    img.style.display = 'none';
    img.alt = '';
    img.src = `https://www.googleadservices.com/pagead/conversion/${GOOGLE_ADS_CONVERSION_ID}/?value=${CONVERSION_VALUE}&currency_code=${CONVERSION_CURRENCY}&label=${GOOGLE_ADS_CONVERSION_LABEL}&guid=ON&script=0`;
    document.body.appendChild(img);
    return () => { if (document.body.contains(img)) document.body.removeChild(img); };
  }, [cityName]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEO
        title={`Thank You – Quote Received | Alliance Security Systems`}
        description={`Your free security quote request for ${cityName} has been received. A certified specialist will contact you within 2 hours. Alliance Security Systems – GTA's most trusted since 1999.`}
        keywords={`security quote received, Alliance Security thank you, free security quote ${cityName}`}
        canonical={`/quote/thank-you?city=${cityParam}`}
      />

      {/* Top bar */}
      <div className="bg-[#0A1628] text-white py-2 px-4 text-center text-xs font-medium">
        <span className="text-[#D4A853] font-bold">✓ Your quote request has been received — we'll be in touch within 2 hours.</span>
        <a href="tel:18884589181" className="underline ml-2 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">
          Need faster? Call 1-888-458-9181
        </a>
      </div>

      {/* Navbar */}
      <nav className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div className="flex flex-col leading-none border-2 border-[#D4A853] rounded-lg px-3 py-2 bg-[#0A1628]">
              <span className="text-xl font-extrabold tracking-wider text-[#D4A853]">ALLIANCE</span>
              <span className="text-[9px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
            </div>
          </Link>
          <a
            href="tel:18884589181"
            className="flex items-center gap-2 text-sm font-semibold text-[#0A1628] hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-7 h-7 bg-[#D4A853] rounded-full flex items-center justify-center">
              <i className="ri-phone-fill text-white text-xs"></i>
            </div>
            1-888-458-9181
          </a>
        </div>
      </nav>

      <main>
        {/* ── HERO CONFIRMATION ── */}
        <section className="relative py-20 sm:py-28 overflow-hidden bg-gradient-to-br from-[#0A1628] via-[#0D1F3C] to-[#0A1628]">
          {/* Dot grid */}
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '28px 28px' }}></div>
          {/* Gold glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4A853]/10 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
            {/* Animated checkmark */}
            <div className="relative inline-flex items-center justify-center mb-8">
              <div className="w-28 h-28 bg-emerald-500/15 rounded-full flex items-center justify-center animate-pulse">
                <div className="w-20 h-20 bg-emerald-500/25 rounded-full flex items-center justify-center">
                  <div className="w-14 h-14 bg-emerald-500 rounded-full flex items-center justify-center shadow-xl shadow-emerald-500/40">
                    <i className="ri-check-double-line text-white text-3xl"></i>
                  </div>
                </div>
              </div>
            </div>

            <div className="inline-flex items-center gap-2 bg-[#D4A853]/15 border border-[#D4A853]/40 text-[#D4A853] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-5 whitespace-nowrap">
              <i className="ri-map-pin-2-fill"></i>
              {cityName.toUpperCase()} QUOTE REQUEST CONFIRMED
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-5">
              Thank You!<br />
              <span className="text-[#D4A853]">Your Quote Is On Its Way</span>
            </h1>

            <p className="text-lg text-white/70 leading-relaxed mb-10 max-w-xl mx-auto">
              A certified Alliance Security specialist in <strong className="text-white">{cityName}</strong> is reviewing your details and will contact you with a personalized quote within <strong className="text-[#D4A853]">2 business hours</strong>.
            </p>

            {/* Trust row */}
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              {TRUST_ITEMS.map((t, i) => (
                <div key={i} className="flex items-center gap-2 bg-white/8 border border-white/15 rounded-full px-4 py-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${t.icon} text-[#D4A853] text-sm`}></i>
                  </div>
                  <span className="text-xs font-semibold text-white/80 whitespace-nowrap">{t.label}</span>
                </div>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:18884589181"
                className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-8 py-4 rounded-full font-bold text-base hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-xl shadow-[#D4A853]/25"
              >
                <i className="ri-phone-line text-lg"></i>
                Call Us Now: 1-888-458-9181
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 border-2 border-white/25 text-white px-8 py-4 rounded-full font-bold text-base hover:border-[#D4A853] hover:text-[#D4A853] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line text-lg"></i>
                Back to Homepage
              </Link>
            </div>
          </div>
        </section>

        {/* ── WHAT HAPPENS NEXT ── */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-4 whitespace-nowrap">WHAT HAPPENS NEXT</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A1628]">Your {cityName} Security Journey Starts Now</h2>
            </div>

            <div className="relative">
              {/* Vertical connector */}
              <div className="hidden lg:block absolute left-[calc(50%-1px)] top-10 bottom-10 w-0.5 bg-gradient-to-b from-[#D4A853]/60 via-[#D4A853]/30 to-transparent"></div>

              <div className="space-y-6">
                {NEXT_STEPS.map((step, i) => (
                  <div
                    key={i}
                    className={`flex flex-col lg:flex-row items-center gap-6 ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                  >
                    <div className={`flex-1 ${i % 2 === 1 ? 'lg:text-right' : 'lg:text-left'}`}>
                      <div className={`bg-[#F8F9FA] rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow ${i % 2 === 1 ? 'lg:ml-8' : 'lg:mr-8'}`}>
                        <h3 className="text-lg font-bold text-[#0A1628] mb-2">{step.title}</h3>
                        <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>

                    {/* Center icon */}
                    <div className="relative z-10 w-16 h-16 bg-[#0A1628] rounded-2xl flex items-center justify-center shadow-xl shrink-0">
                      <i className={`${step.icon} text-2xl text-[#D4A853]`}></i>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#D4A853] rounded-full flex items-center justify-center text-white text-xs font-extrabold">{i + 1}</div>
                    </div>

                    <div className="flex-1 hidden lg:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── WHILE YOU WAIT ── */}
        <section className="py-14 bg-[#F8F9FA]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-4 whitespace-nowrap">WHILE YOU WAIT</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0A1628]">Explore Our Security Solutions</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-alarm-warning-line', title: 'Alarm Systems', desc: 'Wireless & wired alarm packages for every property type.', link: '/alarm-packages', cta: 'View Packages' },
                { icon: 'ri-camera-line', title: 'Security Cameras', desc: '4K HD cameras with night vision, AI detection & cloud storage.', link: '/camera-packages', cta: 'View Cameras' },
                { icon: 'ri-door-lock-line', title: 'Access Control', desc: 'Smart locks, key fobs & biometric entry for homes and offices.', link: '/access-control', cta: 'Learn More' },
                { icon: 'ri-eye-line', title: '24/7 Monitoring', desc: 'ULC-certified monitoring centre — always watching, always ready.', link: '/live-monitoring', cta: 'Learn More' },
              ].map((card, i) => (
                <Link
                  key={i}
                  to={card.link}
                  className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#D4A853]/20 transition-colors">
                    <i className={`${card.icon} text-2xl text-[#D4A853]`}></i>
                  </div>
                  <h3 className="font-bold text-[#0A1628] text-sm mb-2">{card.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed mb-4">{card.desc}</p>
                  <span className="text-xs font-bold text-[#D4A853] flex items-center gap-1 group-hover:gap-2 transition-all whitespace-nowrap">
                    {card.cta} <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── SOCIAL PROOF ── */}
        <section className="py-14 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-4 whitespace-nowrap">TRUSTED BY THOUSANDS</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0A1628]">What {cityName} Customers Say</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { name: 'Sarah K.', city: cityName, rating: 5, text: 'The quote process was incredibly smooth. Within 2 hours I had a detailed proposal and the installation was done the next day. Absolutely professional.', service: 'Full Home Security', seq: 'ty-r1' },
                { name: 'Michael T.', city: cityName, rating: 5, text: 'I submitted the form at 9am and had a call by 10:30am. The specialist was knowledgeable and the pricing was transparent. No surprises at all.', service: 'Alarm + Cameras', seq: 'ty-r2' },
                { name: 'Anita P.', city: cityName, rating: 5, text: 'Best decision I made for my business. Alliance secured our office with cameras and access control. The team was fast, clean, and professional.', service: 'Commercial Security', seq: 'ty-r3' },
              ].map((r, i) => (
                <div key={i} className="bg-[#F8F9FA] rounded-2xl p-6 border border-gray-100">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: r.rating }).map((_, s) => (
                      <i key={s} className="ri-star-fill text-[#D4A853] text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 italic leading-relaxed mb-4">"{r.text}"</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={`https://public.readdy.ai/ai/img_res/cf0245280d6e57bf80b909eea02d032f.jpg${r.seq}&orientation=squarish`}
                      alt={r.name}
                      className="w-10 h-10 rounded-full border-2 border-[#D4A853]/30 object-cover object-top"
                    />
                    <div>
                      <p className="text-xs font-bold text-[#0A1628]">{r.name} · {r.city}</p>
                      <p className="text-[10px] text-gray-400">{r.service}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── BOTTOM CTA ── */}
        <section className="py-14 bg-[#0A1628] relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">Need to Reach Us Sooner?</h2>
            <p className="text-white/60 text-sm mb-7">Our team is available Mon–Fri 8am–8pm · Sat 9am–5pm · 24/7 Emergency Line</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:18884589181"
                className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-8 py-4 rounded-full font-bold text-base hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-xl"
              >
                <i className="ri-phone-line text-lg"></i>
                1-888-458-9181
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border-2 border-white/25 text-white px-8 py-4 rounded-full font-bold text-base hover:border-[#D4A853] hover:text-[#D4A853] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line text-lg"></i>
                Send Us a Message
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex flex-col leading-none cursor-pointer">
            <span className="text-lg font-extrabold tracking-wider text-[#D4A853]">ALLIANCE</span>
            <span className="text-[9px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
          </Link>
          <div className="flex items-center gap-6 text-xs text-white/50">
            <Link to="/privacy-policy" className="hover:text-white/80 cursor-pointer">Privacy Policy</Link>
            <Link to="/contact" className="hover:text-white/80 cursor-pointer">Contact</Link>
            <Link to="/faq" className="hover:text-white/80 cursor-pointer">FAQ</Link>
            <Link to="/get-quote" className="hover:text-white/80 cursor-pointer">Get a Quote</Link>
          </div>
          <p className="text-white/40 text-xs">© 2025 Alliance Security Systems. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
